// FinKloz — Bank Rec Match View
// Shows matched, unmatched-bank, and unmatched-GL transactions.
// Supports manual matching: select items from both sides, match when net = 0.

import React, { useState, useMemo } from 'react';
import { sans, mono, green, greenLight, red, redLight, amber, amberLight, useTheme } from '../tokens';

const teal = '#0D9488';
const tealLight = '#CCFBF1';

function fmt$(n) {
  if (n === 0) return '--';
  const abs = Math.abs(n);
  const str = abs.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `$(${str})` : `$${str}`;
}

function bankNet(txn) { return (txn.credit || 0) - (txn.debit || 0); }
// Use txnDebit/txnCredit (original currency) when available, otherwise CAD
function glNet(txn) {
  if (txn.txnDebit !== undefined || txn.txnCredit !== undefined) {
    return (txn.txnDebit || 0) - (txn.txnCredit || 0);
  }
  return (txn.debit || 0) - (txn.credit || 0);
}

function ConfidenceBadge({ confidence }) {
  const bg = confidence >= 90 ? greenLight : confidence >= 70 ? amberLight : '#FEF3C7';
  const fg = confidence >= 90 ? green : confidence >= 70 ? amber : '#92400E';
  return (
    <span style={{
      fontFamily: mono, fontSize: 10, fontWeight: 600,
      padding: '2px 6px', borderRadius: 3, background: bg, color: fg,
    }}>
      {confidence}%
    </span>
  );
}

function SelectableTxnRow({ txn, side, selected, onToggle, t }) {
  const amount = side === 'bank' ? bankNet(txn) : glNet(txn);
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '22px 72px 1fr 80px',
      gap: 6,
      padding: '5px 4px',
      borderBottom: `1px solid ${t.borderLight}`,
      fontFamily: mono,
      fontSize: 11,
      cursor: 'pointer',
      background: selected ? (side === 'bank' ? '#FEF2F2' : '#FFF7ED') : 'transparent',
      borderRadius: selected ? 4 : 0,
    }}>
      <div
        onClick={() => onToggle(txn.id)}
        style={{
          width: 16, height: 16, borderRadius: 3,
          border: `1.5px solid ${selected ? teal : t.border}`,
          background: selected ? teal : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, marginTop: 1,
        }}
      >
        {selected && <span style={{ color: '#fff', fontSize: 9, fontWeight: 700 }}>{'\u2713'}</span>}
      </div>
      <span onClick={() => onToggle(txn.id)} style={{ color: t.textSecondary }}>{txn.date}</span>
      <span onClick={() => onToggle(txn.id)} style={{ color: t.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {txn.description}
      </span>
      <span onClick={() => onToggle(txn.id)} style={{ textAlign: 'right', color: amount < 0 ? red : t.textPrimary }}>{fmt$(amount)}</span>
    </div>
  );
}

// Load GL accounts from localStorage (TB import)
function getGLAccounts() {
  try {
    // Try common month keys
    const months = ['2026-02', '2026-01', '2026-03', '2025-12'];
    for (const m of months) {
      const raw = localStorage.getItem(`finkloz_gl_mappings_${m}`);
      if (raw) {
        const data = JSON.parse(raw);
        if (data?.accounts?.length > 0) {
          // Deduplicate by code (TB import can have same account in multiple panels)
          const seen = new Set();
          const unique = [];
          for (const a of data.accounts) {
            if (!seen.has(a.code)) {
              seen.add(a.code);
              unique.push(a);
            }
          }
          return unique.sort((a, b) => String(a.code).localeCompare(String(b.code)));
        }
      }
    }
    // Fallback: scan all keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('finkloz_gl_mappings_')) {
        const data = JSON.parse(localStorage.getItem(key));
        if (data?.accounts?.length > 0) {
          const seen = new Set();
          const unique = [];
          for (const a of data.accounts) {
            if (!seen.has(a.code)) { seen.add(a.code); unique.push(a); }
          }
          return unique.sort((a, b) => String(a.code).localeCompare(String(b.code)));
        }
      }
    }
  } catch (e) { void('Failed to load GL accounts:', e); }
  return [];
}

function CreateJEInline({ txn, side, onCreateJE, onCancel, t, glAccounts: propAccounts }) {
  const [offsetAccount, setOffsetAccount] = useState('');
  const [filter, setFilter] = useState('');
  const amount = Math.abs(side === 'bank' ? bankNet(txn) : glNet(txn));
  const isDebit = side === 'bank' ? (txn.credit || 0) > (txn.debit || 0) : (txn.debit || 0) > (txn.credit || 0);

  const allAccounts = useMemo(() => {
    // Use prop if available, otherwise fall back to localStorage
    if (propAccounts && propAccounts.length > 0) {
      const seen = new Set();
      return propAccounts.filter(a => { if (seen.has(a.code)) return false; seen.add(a.code); return true; })
        .sort((a, b) => String(a.code).localeCompare(String(b.code)));
    }
    return getGLAccounts();
  }, [propAccounts]);
  const filtered = useMemo(() => {
    if (!filter) return allAccounts;
    const q = filter.toLowerCase();
    return allAccounts.filter(a =>
      String(a.code).includes(q) || (a.name || '').toLowerCase().includes(q)
    );
  }, [allAccounts, filter]);

  const selectedAcct = allAccounts.find(a => a.code === offsetAccount);

  return (
    <div style={{
      padding: '8px 10px', margin: '4px 0',
      background: '#FFFDE7', border: `1px solid ${amber}`,
      borderRadius: 6,
    }}>
      <div style={{ fontFamily: sans, fontSize: 10, color: t.textMuted, marginBottom: 4 }}>
        Offset account for: {txn.description} ({fmt$(amount)})
      </div>
      <input
        type="text"
        placeholder="Search GL account..."
        value={offsetAccount ? `${selectedAcct?.code || offsetAccount} - ${selectedAcct?.name || ''}` : filter}
        onChange={e => { setFilter(e.target.value); setOffsetAccount(''); }}
        onFocus={() => { if (offsetAccount) { setFilter(''); setOffsetAccount(''); } }}
        style={{
          fontFamily: mono, fontSize: 11, width: '100%',
          padding: '5px 8px', border: `1px solid ${offsetAccount ? teal : t.border}`,
          borderRadius: 4, background: offsetAccount ? greenLight : t.bg,
          marginBottom: 4,
        }}
      />
      {!offsetAccount && (
        <div style={{ maxHeight: 120, overflowY: 'auto', border: `1px solid ${t.border}`, borderRadius: 4, background: t.bg }}>
          {filtered.slice(0, 20).map(a => (
            <div
              key={a.code}
              onClick={() => { setOffsetAccount(a.code); setFilter(''); }}
              style={{
                padding: '4px 8px', cursor: 'pointer',
                fontFamily: mono, fontSize: 11, color: t.textPrimary,
                borderBottom: `1px solid ${t.borderLight}`,
              }}
              onMouseEnter={e => e.currentTarget.style.background = t.bgSurface}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {a.code} - {a.name}
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: '8px', fontFamily: sans, fontSize: 11, color: t.textMuted, textAlign: 'center' }}>
              No accounts found
            </div>
          )}
        </div>
      )}
      {offsetAccount && (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 4 }}>
          <button onClick={onCancel} style={{
            fontFamily: sans, fontSize: 11, padding: '4px 10px',
            border: `1px solid ${t.border}`, borderRadius: 4,
            background: t.bg, color: t.textSecondary, cursor: 'pointer',
          }}>Cancel</button>
          <button onClick={() => onCreateJE({
            date: txn.date, description: txn.description,
            debitAccount: isDebit ? offsetAccount : txn._bankAccountNo || '11010',
            creditAccount: isDebit ? txn._bankAccountNo || '11010' : offsetAccount,
            amount, type: 'manual', sourceItemId: txn.id,
          })} style={{
            fontFamily: sans, fontSize: 11, fontWeight: 600,
            padding: '4px 12px', borderRadius: 4,
            border: `1px solid ${teal}`, background: teal,
            color: '#fff', cursor: 'pointer',
          }}>Create JE</button>
        </div>
      )}
    </div>
  );
}

export default function BankRecMatchView({ matchResult, onManualMatch, onCreateJE, onFlipSign, bankAccountNo, glAccounts }) {
  const t = useTheme();
  const [tab, setTab] = useState('unmatched');
  const [selectedBank, setSelectedBank] = useState(new Set());
  const [selectedGL, setSelectedGL] = useState(new Set());
  const [creatingJEFor, setCreatingJEFor] = useState(null); // txn id

  const { matched, unmatchedBank, unmatchedGL, stats } = matchResult || {};

  // Compute selected totals
  const selectedBankTotal = useMemo(() => {
    if (!unmatchedBank) return 0;
    return unmatchedBank
      .filter(txn => selectedBank.has(txn.id))
      .reduce((sum, txn) => sum + bankNet(txn), 0);
  }, [unmatchedBank, selectedBank]);

  const selectedGLTotal = useMemo(() => {
    if (!unmatchedGL) return 0;
    return unmatchedGL
      .filter(txn => selectedGL.has(txn.id))
      .reduce((sum, txn) => sum + glNet(txn), 0);
  }, [unmatchedGL, selectedGL]);

  if (!matchResult) return null;

  const netDifference = Math.round((Math.abs(selectedBankTotal) - Math.abs(selectedGLTotal)) * 100) / 100;
  const canMatch = selectedBank.size > 0 && selectedGL.size > 0 && Math.abs(netDifference) < 0.10;
  const hasSelections = selectedBank.size > 0 || selectedGL.size > 0;

  const toggleBank = (id) => {
    setSelectedBank(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleGL = (id) => {
    setSelectedGL(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleManualMatch = () => {
    if (!canMatch || !onManualMatch) return;
    const bankItems = unmatchedBank.filter(txn => selectedBank.has(txn.id));
    const glItems = unmatchedGL.filter(txn => selectedGL.has(txn.id));
    onManualMatch(bankItems, glItems);
    setSelectedBank(new Set());
    setSelectedGL(new Set());
  };

  const clearSelections = () => {
    setSelectedBank(new Set());
    setSelectedGL(new Set());
  };

  const tabs = [
    { id: 'unmatched', label: `Unmatched (${unmatchedBank.length + unmatchedGL.length})`, color: unmatchedBank.length + unmatchedGL.length > 0 ? red : t.textMuted },
    { id: 'matched', label: `Matched (${matched.length})`, color: green },
  ];

  return (
    <div>
      {/* Stats bar */}
      <div style={{
        display: 'flex', gap: 16, padding: '8px 12px', marginBottom: 8,
        background: t.bgSurface, borderRadius: 6, fontFamily: sans, fontSize: 12,
      }}>
        <span>Bank: <b>{stats.bankCount}</b></span>
        <span>GL: <b>{stats.glCount}</b></span>
        <span>Matched: <b style={{ color: green }}>{stats.matchedCount}</b></span>
        <span>Rate: <b style={{ color: stats.matchRate >= 90 ? green : stats.matchRate >= 70 ? amber : red }}>{stats.matchRate}%</b></span>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 2, marginBottom: 8 }}>
        {tabs.map(tb => (
          <button
            key={tb.id}
            onClick={() => setTab(tb.id)}
            style={{
              fontFamily: sans, fontSize: 11, fontWeight: tab === tb.id ? 600 : 400,
              padding: '5px 12px', borderRadius: 4,
              border: tab === tb.id ? `1px solid ${tb.color}` : `1px solid ${t.border}`,
              background: tab === tb.id ? (tb.color === green ? greenLight : tb.color === red ? redLight : amberLight) : t.bg,
              color: tab === tb.id ? tb.color : t.textSecondary,
              cursor: 'pointer',
            }}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {/* Unmatched: side-by-side with manual matching */}
      {tab === 'unmatched' && (
        <div>
          {/* Selection toolbar */}
          {hasSelections && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 12px', marginBottom: 8,
              background: canMatch ? greenLight : amberLight,
              border: `1px solid ${canMatch ? green : amber}`,
              borderRadius: 6,
            }}>
              <div style={{ fontFamily: sans, fontSize: 12 }}>
                <span style={{ color: t.textSecondary }}>
                  Bank: <b>{selectedBank.size} sel.</b> ({fmt$(selectedBankTotal)})
                </span>
                <span style={{ margin: '0 8px', color: t.textMuted }}>|</span>
                <span style={{ color: t.textSecondary }}>
                  GL: <b>{selectedGL.size} sel.</b> ({fmt$(selectedGLTotal)})
                </span>
                <span style={{ margin: '0 8px', color: t.textMuted }}>|</span>
                <span style={{ fontWeight: 600, color: canMatch ? green : amber }}>
                  Net: {fmt$(netDifference)}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={clearSelections}
                  style={{
                    fontFamily: sans, fontSize: 11, padding: '4px 10px',
                    border: `1px solid ${t.border}`, borderRadius: 4,
                    background: t.bg, color: t.textSecondary, cursor: 'pointer',
                  }}
                >
                  Clear
                </button>
                <button
                  onClick={handleManualMatch}
                  disabled={!canMatch}
                  style={{
                    fontFamily: sans, fontSize: 11, fontWeight: 600,
                    padding: '4px 12px', borderRadius: 4,
                    border: `1px solid ${canMatch ? green : t.border}`,
                    background: canMatch ? green : t.bgSurface,
                    color: canMatch ? '#fff' : t.textMuted,
                    cursor: canMatch ? 'pointer' : 'not-allowed',
                  }}
                >
                  Match Selected
                </button>
              </div>
            </div>
          )}

          {/* Side-by-side columns */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {/* Bank Only */}
            <div>
              <div style={{
                fontFamily: sans, fontSize: 11, fontWeight: 600, color: red,
                padding: '4px 0', borderBottom: `2px solid ${red}`, marginBottom: 4,
              }}>
                Bank Only ({unmatchedBank.length})
              </div>
              <div style={{ maxHeight: 350, overflowY: 'auto' }}>
                {unmatchedBank.map(txn => (
                  <React.Fragment key={txn.id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <div style={{ flex: 1 }}>
                        <SelectableTxnRow
                          txn={txn}
                          side="bank"
                          selected={selectedBank.has(txn.id)}
                          onToggle={toggleBank}
                          onFlipSign={onFlipSign ? (id) => onFlipSign(id, 'bank') : null}
                          t={t}
                        />
                      </div>
                      {onCreateJE && creatingJEFor !== txn.id && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setCreatingJEFor(txn.id); }}
                          style={{
                            fontFamily: sans, fontSize: 9, fontWeight: 600,
                            padding: '3px 6px', borderRadius: 3,
                            border: `1px solid ${amber}`, background: amberLight,
                            color: amber, cursor: 'pointer', whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          Create JE
                        </button>
                      )}
                    </div>
                    {creatingJEFor === txn.id && (
                      <CreateJEInline
                        txn={{ ...txn, _bankAccountNo: bankAccountNo }}
                        side="bank"
                        glAccounts={glAccounts}
                        onCreateJE={(je) => {
                          onCreateJE(je);
                          setCreatingJEFor(null);
                        }}
                        onCancel={() => setCreatingJEFor(null)}
                        t={t}
                      />
                    )}
                  </React.Fragment>
                ))}
                {unmatchedBank.length === 0 && (
                  <div style={{ fontFamily: sans, fontSize: 11, color: t.textMuted, padding: 12, textAlign: 'center' }}>
                    All bank transactions matched.
                  </div>
                )}
              </div>
            </div>

            {/* GL Only */}
            <div>
              <div style={{
                fontFamily: sans, fontSize: 11, fontWeight: 600, color: amber,
                padding: '4px 0', borderBottom: `2px solid ${amber}`, marginBottom: 4,
              }}>
                GL Only ({unmatchedGL.length})
              </div>
              <div style={{ maxHeight: 350, overflowY: 'auto' }}>
                {unmatchedGL.map(txn => (
                  <SelectableTxnRow
                    key={txn.id}
                    txn={txn}
                    side="gl"
                    selected={selectedGL.has(txn.id)}
                    onFlipSign={onFlipSign ? (id) => onFlipSign(id, 'gl') : null}
                    onToggle={toggleGL}
                    t={t}
                  />
                ))}
                {unmatchedGL.length === 0 && (
                  <div style={{ fontFamily: sans, fontSize: 11, color: t.textMuted, padding: 12, textAlign: 'center' }}>
                    All GL entries matched.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Matched transactions */}
      {tab === 'matched' && (
        <div style={{ maxHeight: 350, overflowY: 'auto' }}>
          {/* Column headers */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 40px 1fr',
            gap: 4, padding: '4px 0', borderBottom: `2px solid ${t.border}`,
            fontFamily: sans, fontSize: 10, fontWeight: 600, color: t.textMuted,
          }}>
            <span>Bank</span>
            <span></span>
            <span>GL</span>
          </div>

          {matched.map((m, i) => {
            // Support both single and batch matches
            const bankTxns = m.bankTxns || (m.bankTxn ? [m.bankTxn] : []);
            const glEntries = m.glEntries || (m.glEntry ? [m.glEntry] : []);
            const isBatch = bankTxns.length > 1 || glEntries.length > 1;
            const glTotal = glEntries.reduce((s, g) => s + Math.abs(glNet(g)), 0);
            const bankTotal = bankTxns.reduce((s, b) => s + Math.abs(bankNet(b)), 0);

            return (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '1fr 40px 1fr',
                gap: 4, padding: '6px 0', borderBottom: `1px solid ${t.borderLight}`,
                alignItems: 'start',
              }}>
                {/* Bank side */}
                <div>
                  {bankTxns.map((b, bi) => (
                    <div key={bi} style={{ marginBottom: bankTxns.length > 1 ? 4 : 0 }}>
                      <div style={{ fontFamily: mono, fontSize: 10, color: t.textSecondary }}>{b.date}</div>
                      <div style={{ fontFamily: mono, fontSize: 11, color: t.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {b.description}
                      </div>
                      <div style={{ fontFamily: mono, fontSize: 11, color: t.textSecondary }}>
                        {fmt$(bankNet(b))}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Confidence */}
                <div style={{ textAlign: 'center', paddingTop: 6 }}>
                  <ConfidenceBadge confidence={m.confidence} />
                </div>
                {/* GL side */}
                <div>
                  {glEntries.map((g, gi) => (
                    <div key={gi} style={{
                      padding: '1px 0',
                      borderBottom: gi < glEntries.length - 1 ? `1px dotted ${t.borderLight}` : 'none',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
                        <span style={{ fontFamily: mono, fontSize: 10, color: t.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                          {g.description}
                        </span>
                        <span style={{ fontFamily: mono, fontSize: 10, color: t.textSecondary, flexShrink: 0 }}>
                          {fmt$(glNet(g))}
                        </span>
                      </div>
                    </div>
                  ))}
                  {isBatch && glEntries.length > 1 && (
                    <div style={{
                      fontFamily: mono, fontSize: 10, fontWeight: 600,
                      color: t.textPrimary, textAlign: 'right',
                      paddingTop: 3, borderTop: `1px solid ${t.border}`, marginTop: 2,
                    }}>
                      Total: {fmt$(glTotal * (glNet(glEntries[0]) < 0 ? -1 : 1))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {matched.length === 0 && (
            <div style={{ fontFamily: sans, fontSize: 12, color: t.textMuted, padding: 20, textAlign: 'center' }}>
              No matched transactions yet.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
